(() => {
  const root = document.documentElement;

  const cursor = document.querySelector('[data-site-cursor]');
  const hasFinePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;

  if (cursor && hasFinePointer) {
    let targetX = -50;
    let targetY = -50;
    let currentX = -50;
    let currentY = -50;

    const moveCursor = () => {
      currentX += (targetX - currentX) * .2;
      currentY += (targetY - currentY) * .2;
      cursor.style.transform = `translate3d(${currentX + 5}px, ${currentY + 7}px, 0) translate(-50%, -50%)`;
      requestAnimationFrame(moveCursor);
    };

    document.addEventListener('mousemove', event => {
      targetX = event.clientX;
      targetY = event.clientY;
      cursor.classList.add('is-visible');
      cursor.classList.toggle('is-hovering', Boolean(event.target.closest('a, button, [role="button"], summary, input, select, textarea, label')));
    }, { passive: true });

    document.addEventListener('mousedown', () => cursor.classList.add('is-pressed'));
    document.addEventListener('mouseup', () => cursor.classList.remove('is-pressed'));
    document.documentElement.addEventListener('mouseleave', () => cursor.classList.remove('is-visible'));
    document.documentElement.addEventListener('mouseenter', () => cursor.classList.add('is-visible'));
    requestAnimationFrame(moveCursor);
  }

  const header = document.querySelector('[data-header]');
  const setHeader = () => header?.classList.toggle('is-scrolled', window.scrollY > 30);
  setHeader();
  addEventListener('scroll', setHeader, { passive: true });

  const scrollProgress = document.querySelector('[data-scroll-progress]');
  const scrollProgressTrack = scrollProgress?.parentElement;
  let scrollProgressFrame = 0;

  const updateScrollProgress = () => {
    scrollProgressFrame = 0;
    if (!scrollProgress) return;

    const scrollableDistance = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const progress = scrollableDistance > 0
      ? Math.min(1, Math.max(0, window.scrollY / scrollableDistance))
      : 1;

    scrollProgress.style.transform = `scaleX(${progress})`;
    scrollProgressTrack?.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
  };

  const requestScrollProgressUpdate = () => {
    if (scrollProgressFrame) return;
    scrollProgressFrame = requestAnimationFrame(updateScrollProgress);
  };

  updateScrollProgress();
  window.addEventListener('scroll', requestScrollProgressUpdate, { passive: true });
  window.addEventListener('resize', requestScrollProgressUpdate);
  window.addEventListener('load', requestScrollProgressUpdate, { once: true });

  const toggle = document.querySelector('[data-menu-toggle]');
  const menu = document.querySelector('[data-mobile-menu]');

  const setMenuOpen = open => {
    if (!toggle || !menu) return;
    toggle.setAttribute('aria-expanded', String(open));
    menu.setAttribute('aria-hidden', String(!open));
    menu.classList.toggle('is-open', open);
    root.classList.toggle('menu-open', open);
    if (open) menu.scrollTop = 0;
  };

  if (menu) menu.setAttribute('aria-hidden', 'true');

  toggle?.addEventListener('click', () => {
    setMenuOpen(toggle.getAttribute('aria-expanded') !== 'true');
  });

  menu?.addEventListener('click', event => {
    if (event.target.closest('a')) setMenuOpen(false);
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && toggle?.getAttribute('aria-expanded') === 'true') {
      setMenuOpen(false);
      toggle.focus();
    }
  });

  window.addEventListener('resize', () => {
    if (window.innerWidth > 1100 && toggle?.getAttribute('aria-expanded') === 'true') {
      setMenuOpen(false);
    }
  });

  const reveal = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        reveal.unobserve(entry.target);
      }
    });
  }, { threshold: .12, rootMargin: '0px 0px -40px' });
  document.querySelectorAll('.reveal').forEach(el => reveal.observe(el));

  document.querySelectorAll('[data-hero]').forEach(hero => {
    const slides = [...hero.querySelectorAll('[data-slide]')];
    if (slides.length < 2) return;
    const current = hero.querySelector('[data-current]');
    const progress = hero.querySelector('[data-progress]');
    let index = 0;
    let timer;
    const duration = 7000;
    const show = next => {
      slides[index].classList.remove('is-active');
      index = (next + slides.length) % slides.length;
      slides[index].classList.add('is-active');
      if (current) current.textContent = String(index + 1).padStart(2, '0');
      if (progress) {
        progress.style.animation = 'none';
        progress.offsetHeight;
        progress.style.animation = `heroProgress ${duration}ms linear`;
      }
      clearTimeout(timer);
      timer = setTimeout(() => show(index + 1), duration);
    };
    hero.querySelector('[data-next]')?.addEventListener('click', () => show(index + 1));
    hero.querySelector('[data-prev]')?.addEventListener('click', () => show(index - 1));
    show(0);
  });

  document.querySelector('[data-sort-by]')?.addEventListener('change', event => {
    const url = new URL(location.href);
    url.searchParams.set('sort_by', event.target.value);
    location.href = url.toString();
  });

  const filters = document.querySelector('[data-filters]');
  document.querySelector('[data-filter-toggle]')?.addEventListener('click', () => {
    filters?.classList.add('is-open');
    root.classList.add('filters-open');
  });
  document.querySelector('[data-filter-close]')?.addEventListener('click', () => {
    filters?.classList.remove('is-open');
    root.classList.remove('filters-open');
  });

  document.querySelectorAll('[data-gallery-thumb]').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const index = thumb.dataset.galleryThumb;
      document.querySelectorAll('[data-gallery-thumb]').forEach(el => el.classList.toggle('is-active', el === thumb));
      document.querySelectorAll('[data-gallery-slide]').forEach(slide => slide.classList.toggle('is-active', slide.dataset.gallerySlide === index));
    });
  });

  const lightbox = document.querySelector('[data-gallery-lightbox]');
  const lightboxImage = document.querySelector('[data-gallery-lightbox-image]');
  const lightboxVideo = document.querySelector('[data-gallery-lightbox-video]');
  const lightboxTitle = document.querySelector('[data-gallery-lightbox-title]');
  const lightboxCaption = document.querySelector('[data-gallery-lightbox-caption]');
  const lightboxCounter = document.querySelector('[data-gallery-counter]');
  const previousButton = document.querySelector('[data-gallery-previous]');
  const nextButton = document.querySelector('[data-gallery-next]');
  let lightboxImages = [];
  let lightboxIndex = 0;

  const renderLightboxImage = () => {
    if (!lightboxImage || !lightboxImages.length) return;
    const media = lightboxImages[lightboxIndex];
    lightboxVideo?.pause();
    if (media.type === 'video' && lightboxVideo) {
      lightboxImage.hidden = true;
      lightboxImage.removeAttribute('src');
      lightboxVideo.hidden = false;
      lightboxVideo.src = media.src;
      lightboxVideo.load();
    } else {
      if (lightboxVideo) {
        lightboxVideo.hidden = true;
        lightboxVideo.removeAttribute('src');
      }
      lightboxImage.hidden = false;
      lightboxImage.src = media.src;
      lightboxImage.alt = media.alt || 'Gallery image';
    }
    if (lightboxCounter) lightboxCounter.textContent = `${lightboxIndex + 1} / ${lightboxImages.length}`;
    const hasMultipleImages = lightboxImages.length > 1;
    if (previousButton) previousButton.hidden = !hasMultipleImages;
    if (nextButton) nextButton.hidden = !hasMultipleImages;
  };

  const moveLightbox = direction => {
    if (lightboxImages.length < 2) return;
    lightboxIndex = (lightboxIndex + direction + lightboxImages.length) % lightboxImages.length;
    renderLightboxImage();
  };

  const closeLightbox = () => {
    if (!lightbox) return;
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    root.classList.remove('gallery-open');
    lightboxVideo?.pause();
    lightboxVideo?.removeAttribute('src');
    if (lightboxImage) {
      lightboxImage.hidden = true;
      lightboxImage.removeAttribute('src');
    }
    lightboxImages = [];
  };

  document.querySelectorAll('[data-gallery-open]').forEach(item => {
    item.addEventListener('click', () => {
      lightboxImages = [...item.querySelectorAll('[data-gallery-lightbox-slide]')]
        .map(slide => ({ src: slide.dataset.mediaUrl, alt: slide.dataset.alt, type: slide.dataset.mediaType || 'image' }))
        .filter(image => image.src);
      if (!lightboxImages.length || !lightbox || !lightboxImage) return;

      lightboxIndex = 0;
      if (lightboxTitle) lightboxTitle.textContent = item.dataset.title || '';
      if (lightboxCaption) lightboxCaption.textContent = item.dataset.caption || '';
      renderLightboxImage();
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      root.classList.add('gallery-open');
      document.querySelector('[data-gallery-close]')?.focus();
    });
  });

  previousButton?.addEventListener('click', () => moveLightbox(-1));
  nextButton?.addEventListener('click', () => moveLightbox(1));
  document.querySelector('[data-gallery-close]')?.addEventListener('click', closeLightbox);
  lightbox?.addEventListener('click', event => {
    if (event.target === lightbox) closeLightbox();
  });
  addEventListener('keydown', event => {
    if (event.key === 'Escape') closeLightbox();
    if (!lightbox?.classList.contains('is-open')) return;
    if (event.key === 'ArrowLeft') moveLightbox(-1);
    if (event.key === 'ArrowRight') moveLightbox(1);
  });
})();
